import { ldb } from '@codesuma/baseline'
import calendar, { IDayCell } from './calendar'
import occasions from './occasions'
import customOccasions from './custom-occasions'
import countryOccasions from './country-occasions'
import overrides from './occasion-overrides'
import { dayMarks } from './day-holidays'
import settings from './settings'
import { J_WEEKDAYS, G_WEEKDAYS, J_MONTHS, G_MONTHS, G_MONTHS_EN, H_MONTHS } from '../configs/names'
import { monthColor, THEME_COLORS } from '../configs/colors'
import { toFaDigits } from '../helpers/digits'
import { solarYear } from '../helpers/year'

// Feeds the Android and iOS home-screen widgets.
//
// A widget cannot run the app: Android draws it from RemoteViews in the
// launcher's process, iOS from a WidgetKit extension, and neither has a
// WebView or this bundle. So the widget is given finished text rather than
// dates to work out — every string here is already in the user's calendar,
// in the user's year format, in Persian digits. The native side looks up
// today's key and paints what it finds.
//
// That keeps the calendar in one place. Porting the Jalaali and hijri
// conversions to Java or Swift would give a second copy that disagrees with
// the app the first time a moon-sighting correction lands.
//
// The plugin is reached through window.Capacitor rather than imported, the way
// the reminders and theme services reach theirs: it belongs to the native
// shell, not to a bundle that also has to run in a browser.

// How far ahead the payload runs. The widget redraws itself at midnight from
// whatever it was last given, so this is how long it keeps telling the truth
// with the app never opened — the same year-and-a-bit horizon the reminder
// scheduler works to (see services/occurrences.ts).
const DAYS = 400

// One day, as the widget will show it.
interface IWidgetDay {
    wd: string // weekday name
    n: string // the day number, large
    my: string // month and year
    s: string // the same day in the other two calendars
    o: string // occasion titles, joined; '' when the day has none
    h: 1 | 0 // holiday: the number is written in the holiday ink below
    c: string // the paper the card is drawn on, as --paper-rgb channels
    i: string // the ink every line is written in, as --ink-rgb channels
    k: string // what the number turns on a holiday, already opaque
}

const capacitor = () => (window as any).Capacitor
const plugin = () => capacitor()?.Plugins?.Widget

// The seasons theme darkens a holiday number rather than reddening it — red
// vanishes into half of the twelve month colors. That is rgba(0, 0, 0, .55) in
// CSS, and the widget paints opaque colors, so the black is composited here
// over the month the day falls in.
const darken = (rgb: string) =>
    rgb.split(',').map(n => Math.round(Number(n) * 0.45)).join(', ')

function dayOf(day: IDayCell & { wd: number }): IWidgetDay {
    const { system, theme } = settings.get()
    const gregorian = system === 'gregorian'

    const jalaaliLine = `${toFaDigits(day.jd)} ${J_MONTHS[day.jm - 1]} ${solarYear(day.jy)}`
    const hijriLine = `${toFaDigits(day.hd)} ${H_MONTHS[day.hm - 1]} ${toFaDigits(day.hy)}`
    // The day page's spelling: the other solar calendar first, then the hijri,
    // with the Gregorian date written in Persian month names and Latin digits.
    const gregorianLine = `${day.gd} ${G_MONTHS[day.gm - 1]} ${day.gy}`

    // As the reader has left them on the day page — their own names, and without
    // the ones they put away. The holiday flag below still asks the untouched
    // list, through dayMarks.
    const built = overrides.builtIns(occasions.forDay(day)).shown.map(o => o.title)
    const abroad = overrides.countries(countryOccasions.forDay(day)).shown.map(o => o.title)
    const mine = customOccasions.forDay(day).map(o => o.title)
    // The same order the day page uses: whichever calendar is being read leads.
    const titles = gregorian ? [...abroad, ...built, ...mine] : [...built, ...abroad, ...mine]
    // The weekend follows the calendar on screen, as it does in the month grid:
    // جمعه alone in Jalaali, Saturday and Sunday in Gregorian.
    const weekend = gregorian ? day.wd <= 1 : day.wd === 6

    // The card wears the theme the app is on. The seasons theme has no single
    // paper — it is whichever month the day falls in — so those cards keep the
    // month color and the white ink they have always had.
    const flat = theme === 'seasons' ? null : THEME_COLORS[theme]
    const paper = flat ? flat.paper : monthColor(gregorian ? day.gm : day.jm, system)
    const ink = flat ? flat.ink : '255, 255, 255'
    const holiday = flat ? flat.holiday : darken(paper)

    return {
        wd: gregorian ? G_WEEKDAYS[day.wd] : J_WEEKDAYS[day.wd],
        n: gregorian ? String(day.gd) : toFaDigits(day.jd),
        my: gregorian
            ? `${G_MONTHS_EN[day.gm - 1]} ${day.gy}`
            : `${J_MONTHS[day.jm - 1]} ${solarYear(day.jy)}`,
        s: `${gregorian ? jalaaliLine : gregorianLine} | ${hijriLine}`,
        o: titles.join(' • '),
        // The card has one holiday ink and no second one, so only the calendar
        // being read colours a day here. A day off in the other one is listed in
        // `o` but not coloured; a second ink needs the Java and Swift side.
        h: dayMarks(day, system, weekend).primary ? 1 : 0,
        c: paper,
        i: ink,
        k: holiday,
    }
}

// Keyed by Gregorian date, in the shape the /day?d= query already uses.
function build(): Record<string, IWidgetDay> {
    const t = calendar.today()
    const out: Record<string, IWidgetDay> = {}
    for (let i = 0; i < DAYS; i++) {
        const day = calendar.dayAt(t.gy, t.gm, t.gd, i)
        out[`${day.gy}-${day.gm}-${day.gd}`] = dayOf(day)
    }
    return out
}

// A fingerprint of the table the widget was last given, kept under this key.
// The fingerprint and not the table itself: 75KB held twice over would cost
// more than the comparison saves.
const LAST = 'widget-mark'

// Long enough after launch to be clear of it. Only ever waited out on a phone
// whose widget has never been given anything.
const FIRST_RUN_MS = 3000

// FNV-1a. Enough to tell two builds apart, which is all that is asked of it —
// it never has to survive a version of the app, only the last session.
function fingerprint(table: string): string {
    let h = 0x811c9dc5
    for (let i = 0; i < table.length; i++) h = Math.imul(h ^ table.charCodeAt(i), 0x01000193)
    return (h >>> 0).toString(36)
}

// Rebuilt whole rather than diffed: it is one write of about 75KB, and a
// changed setting or a moved hijri month can rewrite every row of it.
//
// Handing it over is the expensive half, not building it. The string crosses
// the bridge, is written to the container the widget shares, and then the
// widget's own process is woken to redraw it — so a table that says exactly
// what the last one said is not worth sending, and this is where that is
// decided.
function flush() {
    const days = JSON.stringify(build())
    const mark = fingerprint(days)
    // Through String(), because ldb parses whatever it reads back: a
    // fingerprint that happens to be all digits returns as a number, and would
    // never match the string it was written from.
    if (mark === String(ldb.get(LAST))) return
    ldb.set(LAST, mark)
    plugin()?.save({ days })
}

export default {
    init() {
        if (!plugin()) return // web, or a native build without the widget
        // Nothing on the way in. The widget is already holding the table the
        // last session left it — good for DAYS ahead, and it redraws itself at
        // midnight from that — so a launch has nothing to tell it. Waking its
        // process to say so cost the app a beat of its own start.
        //
        // The table goes over on the way out instead: that is when the widget
        // is about to be looked at, and when the app has nothing else to do.
        // Every change made in between is in the table by then, so no change
        // is lost — it simply travels once rather than per event.
        capacitor()?.Plugins?.App?.addListener?.('appStateChange',
            ({ isActive }: { isActive: boolean }) => { if (!isActive) flush() })
        // Unless it has never been given one at all, in which case the card
        // reads "open the app" until something is sent. So send it — once the
        // launch is over.
        // Empty and not null: ldb answers a key it does not hold with ''.
        if (!ldb.get(LAST)) setTimeout(flush, FIRST_RUN_MS)
    },

    // A browser has no home screen to put a card on, so nothing offers one
    // there — see the menu.
    available: () => !!plugin(),

    // Whether a card is already out on a home screen. False on web, and on a
    // platform that will not say.
    async installed(): Promise<boolean> {
        const res = await plugin()?.isInstalled?.()
        return !!res?.installed
    },

    // Android pins the card itself and answers true. iOS cannot, answers
    // false, and the page shows the steps by hand instead.
    //
    // The table goes over first either way. A card pinned from here would
    // otherwise draw "open the app" until the next time the app is
    // backgrounded, which is the worst possible first look at the feature.
    async pin(): Promise<boolean> {
        flush()
        const res = await plugin()?.requestPin?.()
        return !!res?.supported
    },
}
